import { ComentarioSeccion } from '@/components/dominio/ComentarioSeccion';
import { DataGrid } from '@/components/dominio/DataGrid';
import { MontoCRC } from '@/components/dominio/MontoCRC';
import { SelectorPeriodo } from '@/components/dominio/SelectorPeriodo';
import { Card, CardContent } from '@/components/ui/card';
import { usePeriodo } from '@/hooks/usePeriodo';
import { useMovimientos } from '@/hooks/useUtilidades';
import type { FilaMovimientoConUtilidad } from '@adelante/shared';
import { formatearPeriodo, mesAnclaPeriodo } from '@adelante/shared';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

const col = createColumnHelper<FilaMovimientoConUtilidad>();

const COLUMNAS: ColumnDef<FilaMovimientoConUtilidad, unknown>[] = [
  col.accessor('fecha_movimiento', {
    header: 'Fecha',
    meta: { label: 'Fecha' },
    cell: (info) => new Date(info.getValue<Date>()).toLocaleDateString('es-CR'),
    // Filtra contra la fecha YA formateada (DD/MM/YYYY) para que "25/5" o "5/2026" funcionen.
    // El valor crudo es ISO ("2026-05-25T..."), así que sin esto el filtro era inútil.
    filterFn: (row, columnId, filterValue) => {
      const raw = row.getValue<Date | string>(columnId);
      if (raw == null) return false;
      const formateada = new Date(raw).toLocaleDateString('es-CR');
      return formateada.toLowerCase().includes(String(filterValue).toLowerCase());
    },
  }),
  col.accessor('codigo_lote', {
    header: 'Lote',
    meta: { label: 'Lote' },
    cell: (info) => info.getValue() ?? '—',
    filterFn: 'includesString',
  }),
  col.accessor('abreviatura_proyecto', {
    header: 'Proyecto',
    meta: { label: 'Proyecto' },
    cell: (info) => info.getValue() ?? '—',
    filterFn: 'includesString',
  }),
  col.accessor('cliente', {
    header: 'Cliente',
    meta: { label: 'Cliente' },
    cell: (info) => (
      <span className="block max-w-[200px] truncate" title={info.getValue<string>() ?? undefined}>
        {info.getValue() ?? '—'}
      </span>
    ),
    filterFn: 'includesString',
  }),
  col.accessor('nombre_tipo', {
    header: 'Tipo',
    meta: { label: 'Tipo' },
    filterFn: 'includesString',
  }),
  col.accessor('categoria_tipo', {
    header: 'Categoría',
    meta: { label: 'Categoría' },
    cell: (info) => info.getValue() ?? '—',
    filterFn: 'includesString',
  }),
  col.accessor('monto_mov_bruto_crc', {
    header: 'Bruto',
    meta: { label: 'Bruto bancario' },
    cell: (info) => <MontoCRC monto={Number(info.getValue())} />,
    enableColumnFilter: false,
  }),
  col.accessor('monto_qfi_crc', {
    header: 'QFI',
    meta: { label: 'Monto QFI' },
    cell: (info) => {
      const v = info.getValue<number | null>();
      return v != null && v !== 0 ? (
        <MontoCRC monto={Number(v)} />
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    },
    enableColumnFilter: false,
  }),
  col.accessor('monto_gm_crc', {
    header: 'GM',
    meta: { label: 'Monto GM (incl. exclusividad)' },
    cell: (info) => {
      const v = info.getValue<number | null>();
      return v != null && v !== 0 ? (
        <MontoCRC monto={Number(v)} />
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    },
    enableColumnFilter: false,
  }),
  col.accessor('monto_ad_lote_interno_crc', {
    header: 'Comis. 5%',
    meta: { label: 'Comisión 5% (s/ lote interno)' },
    cell: (info) => {
      const v = info.getValue<number | null>();
      return v != null && v !== 0 ? (
        <MontoCRC monto={Number(v)} />
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    },
    enableColumnFilter: false,
  }),
  col.accessor('ingreso_neto_ad_crc', {
    header: 'Neto AD',
    meta: { label: 'Ingreso Neto AD (= Bruto − QFI − GM − Comisión)' },
    cell: (info) => <MontoCRC monto={Number(info.getValue())} />,
    enableColumnFilter: false,
  }),
  col.accessor('monto_base_utilidad_crc', {
    header: 'Base Util.',
    meta: { label: 'Base Utilidad' },
    cell: (info) => {
      const v = info.getValue<number | null>();
      return v != null ? (
        <MontoCRC monto={Number(v)} />
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    },
    enableColumnFilter: false,
  }),
  col.accessor('pct_utilidad', {
    header: '% Util.',
    meta: { label: '% Utilidad' },
    cell: (info) => {
      const v = info.getValue<number | null>();
      return v != null ? (
        <span className="font-mono tabular-nums">{Number(v).toFixed(1)}%</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    },
    enableColumnFilter: false,
  }),
  // Utilidad: usamos UtilidadAD_CRC (calculada on-the-fly) como UNA SOLA fuente
  // de verdad — siempre presente si el caso tiene PorcentajeUtilidadP. La
  // versión "Util. Reservada" (lo persistido por el sistema operativo en
  // dbo.Movimientos.UtilidadReservada) queda oculta por default; en los
  // casos sanos coincide con la calculada, y cuando difiere es porque el
  // sistema operativo no persistió (bug operativo).
  //
  // Caso especial — "Lote interno": cuando MontoAplicadoLote == LoteInterno
  // (típicamente Desembolso 1 o 2 grandes), MontoBaseUtilidad queda en 0 y por
  // ende Utilidad e Indirecto calculados son 0 aunque haya % en el caso. Es
  // comportamiento legítimo del sistema operativo, lo marcamos visualmente
  // para que no se confunda con un caso huérfano sin datos.
  col.accessor('utilidad_ad_crc', {
    header: 'Utilidad',
    meta: { label: 'Utilidad' },
    cell: (info) => {
      const v = info.getValue<number | null>();
      const row = info.row.original;
      // Caso "lote interno": % existe pero la base es 0 → no genera utilidad
      if (
        v === 0 &&
        row.pct_utilidad != null &&
        (row.monto_base_utilidad_crc === 0 || row.monto_base_utilidad_crc === null)
      ) {
        return (
          <span
            className="text-xs italic text-muted-foreground"
            title="Cubre lote interno — no genera utilidad"
          >
            Lote interno
          </span>
        );
      }
      return v != null ? (
        <MontoCRC monto={Number(v)} />
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    },
    enableColumnFilter: false,
  }),
  col.accessor('utilidad_reservada_persistida', {
    header: 'Util. Reservada',
    meta: { label: 'Utilidad Reservada (persistida)' },
    cell: (info) => {
      const v = info.getValue<number | null>();
      return v != null ? (
        <MontoCRC monto={Number(v)} />
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    },
    enableColumnFilter: false,
  }),
  col.accessor('pct_indirecto', {
    header: '% Indir.',
    meta: { label: '% Indirecto' },
    cell: (info) => {
      const v = info.getValue<number | null>();
      return v != null ? (
        <span className="font-mono tabular-nums">{Number(v).toFixed(1)}%</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    },
    enableColumnFilter: false,
  }),
  col.accessor('indirecto_ad_crc', {
    header: 'Indirecto',
    meta: { label: 'Indirecto' },
    cell: (info) => {
      const v = info.getValue<number | null>();
      const row = info.row.original;
      // Caso "lote interno": idem utilidad (ver comentario arriba)
      if (
        v === 0 &&
        row.pct_indirecto != null &&
        (row.monto_base_utilidad_crc === 0 || row.monto_base_utilidad_crc === null)
      ) {
        return (
          <span
            className="text-xs italic text-muted-foreground"
            title="Cubre lote interno — no genera indirecto"
          >
            Lote interno
          </span>
        );
      }
      return v != null ? (
        <MontoCRC monto={Number(v)} />
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    },
    enableColumnFilter: false,
  }),
  col.accessor('depositante', {
    header: 'Depositante',
    meta: { label: 'Depositante' },
    cell: (info) => info.getValue() ?? '—',
    filterFn: 'includesString',
  }),
  col.accessor('detalle_transferencia', {
    header: 'Detalle',
    meta: { label: 'Detalle Transferencia' },
    cell: (info) => (
      <span
        className="block max-w-[180px] truncate text-muted-foreground"
        title={info.getValue<string | null>() ?? undefined}
      >
        {info.getValue() ?? '—'}
      </span>
    ),
    filterFn: 'includesString',
  }),
  col.accessor('completado', {
    header: 'Completado',
    meta: { label: 'Completado' },
    cell: (info) => (info.getValue() ? 'Sí' : 'No'),
    filterFn: 'includesString',
    enableHiding: true,
  }),
];

// Columnas ocultas por defecto (disponibles vía "Columnas")
// - utilidad_reservada_persistida: redundante con `utilidad_ad_crc` ("Utilidad").
//   Coinciden cuando ambas existen; útil solo para auditoría de inconsistencias.
const VISIBILIDAD_INICIAL: Record<string, boolean> = {
  categoria_tipo: false,
  monto_base_utilidad_crc: false,
  utilidad_reservada_persistida: false,
  depositante: false,
  completado: false,
};

export function MovimientosUtilPantalla() {
  const { periodo, setModo, setAnio, setMes, setTrimestre, setDesde, setHasta } = usePeriodo();
  const { data, isLoading, error } = useMovimientos(periodo);

  const movimientos = useMemo(() => data?.movimientos ?? [], [data]);
  const etiquetaPeriodo = formatearPeriodo(periodo);
  const ancla = mesAnclaPeriodo(periodo);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Movimientos + Utilidad Reservada
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{etiquetaPeriodo}</p>
        </div>
        <SelectorPeriodo
          periodo={periodo}
          onSetModo={setModo}
          onSetAnio={setAnio}
          onSetMes={setMes}
          onSetTrimestre={setTrimestre}
          onSetDesde={setDesde}
          onSetHasta={setHasta}
        />
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6 text-destructive">{error.message}</CardContent>
        </Card>
      )}

      <DataGrid
        data={movimientos}
        columns={COLUMNAS}
        isLoading={isLoading}
        pageSize={50}
        defaultColumnVisibility={VISIBILIDAD_INICIAL}
        exportar={{
          titulo: 'Movimientos + Utilidad Reservada',
          subtitulo: etiquetaPeriodo,
        }}
      />

      <ComentarioSeccion
        anio={ancla.anio}
        mes={ancla.mes}
        scope="seccion"
        seccionId="movimientos"
      />
    </div>
  );
}
